import { useState, useEffect } from "react";
import axios from "axios";
import ExcelExportButton from "../components/ExcelExportButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Boxes, Loader2 } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Molds() {
  const [molds, setMolds] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMold, setEditingMold] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    part_id: "",
    cavities: "",
    cycle_time: "",
    runner_weight: "",
    runner_type: "hot",
    setup_time: "",
    purge_time: "",
    mold_life: "",
    observations: "",
  });

  const fetchData = async () => {
    try {
      const [moldsRes, partsRes] = await Promise.all([
        axios.get(`${API}/molds`),
        axios.get(`${API}/parts`),
      ]);
      setMolds(moldsRes.data);
      setParts(partsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      part_id: "",
      cavities: "",
      cycle_time: "",
      runner_weight: "",
      runner_type: "hot",
      setup_time: "",
      purge_time: "",
      mold_life: "",
      observations: "",
    });
    setEditingMold(null);
  };

  const handleOpenDialog = (mold = null) => {
    if (mold) {
      setEditingMold(mold);
      setFormData({
        code: mold.code,
        description: mold.description || "",
        part_id: mold.part_id,
        cavities: mold.cavities.toString(),
        cycle_time: mold.cycle_time.toString(),
        runner_weight: mold.runner_weight.toString(),
        runner_type: mold.runner_type,
        setup_time: mold.setup_time?.toString() || "30",
        purge_time: mold.purge_time?.toString() || "15",
        mold_life: mold.mold_life.toString(),
        observations: mold.observations || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      code: formData.code,
      description: formData.description,
      part_id: formData.part_id,
      cavities: parseInt(formData.cavities),
      cycle_time: parseFloat(formData.cycle_time),
      runner_weight: parseFloat(formData.runner_weight),
      runner_type: formData.runner_type,
      setup_time: parseInt(formData.setup_time) || 30,
      purge_time: parseInt(formData.purge_time) || 15,
      mold_life: parseInt(formData.mold_life),
      observations: formData.observations || null,
    };

    try {
      if (editingMold) {
        await axios.put(`${API}/molds/${editingMold.id}`, payload);
        toast.success("Molde atualizado com sucesso");
      } else {
        await axios.post(`${API}/molds`, payload);
        toast.success("Molde criado com sucesso");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      showError(error, "Erro ao guardar molde");
    }
  };

  const handleDelete = async (moldId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este molde?")) return;
    
    try {
      await axios.delete(`${API}/molds/${moldId}`);
      toast.success("Molde eliminado");
      fetchData();
    } catch (error) {
      showError(error, "Erro ao eliminar molde");
    }
  };

  const getPartName = (partId) => {
    const part = parts.find((p) => p.id === partId);
    return part ? `${part.code} - ${part.description}` : "N/A";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="molds-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight">
            Gestão de Moldes
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure os moldes de injeção
          </p>
        </div>
        <div className="flex gap-2">
          <ExcelExportButton endpoint="molds" label="Excel" />
          <Button variant="outline" onClick={() => window.location.href = "/molds-qr-labels"} data-testid="mold-qr-labels-btn">
            Crachás QR
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} data-testid="add-mold-btn">
              <Plus className="h-4 w-4 mr-2" />
              Novo Molde
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo">
                {editingMold ? "Editar Molde" : "Novo Molde"}
              </DialogTitle>
              <DialogDescription>
                Preencha os dados do molde
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Código *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="Ex: M001"
                    required
                    data-testid="mold-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descrição *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ex: Molde Tampa 50mm"
                    required
                    data-testid="mold-description"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="part_id">Peça Associada *</Label>
                <select
                  id="part_id"
                  value={formData.part_id}
                  onChange={(e) => setFormData({ ...formData, part_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                  data-testid="mold-part"
                >
                  <option value="">Selecione uma peça</option>
                  {parts.map((part) => (
                    <option key={part.id} value={part.id}>
                      {part.code} - {part.description}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cavities">Nº Cavidades *</Label>
                  <Input
                    id="cavities"
                    type="number"
                    min="1"
                    value={formData.cavities}
                    onChange={(e) => setFormData({ ...formData, cavities: e.target.value })}
                    placeholder="8"
                    required
                    data-testid="mold-cavities"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cycle_time">Tempo Ciclo (s) *</Label>
                  <Input
                    id="cycle_time"
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.cycle_time}
                    onChange={(e) => setFormData({ ...formData, cycle_time: e.target.value })}
                    placeholder="12.5"
                    required
                    data-testid="mold-cycle-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="runner_weight">Peso Canal (g) *</Label>
                  <Input
                    id="runner_weight"
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.runner_weight}
                    onChange={(e) => setFormData({ ...formData, runner_weight: e.target.value })}
                    placeholder="15"
                    required
                    data-testid="mold-runner-weight"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="runner_type">Tipo de Canal *</Label>
                  <select
                    id="runner_type"
                    value={formData.runner_type}
                    onChange={(e) => setFormData({ ...formData, runner_type: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="mold-runner-type"
                  >
                    <option value="hot">Quente</option>
                    <option value="cold">Frio</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mold_life">Vida Útil (ciclos) *</Label>
                  <Input
                    id="mold_life"
                    type="number"
                    min="1"
                    value={formData.mold_life}
                    onChange={(e) => setFormData({ ...formData, mold_life: e.target.value })}
                    placeholder="500000"
                    required
                    data-testid="mold-life"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="setup_time">Tempo Setup (min) *</Label>
                  <Input
                    id="setup_time"
                    type="number"
                    min="0"
                    value={formData.setup_time}
                    onChange={(e) => setFormData({ ...formData, setup_time: e.target.value })}
                    placeholder="45"
                    required
                    data-testid="mold-setup-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="purge_time">Tempo Purga (min) *</Label>
                  <Input
                    id="purge_time"
                    type="number"
                    min="0"
                    value={formData.purge_time}
                    onChange={(e) => setFormData({ ...formData, purge_time: e.target.value })}
                    placeholder="15"
                    required
                    data-testid="mold-purge-time"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-mold-btn">
                  {editingMold ? "Guardar Alterações" : "Criar Molde"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Table */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2">
            <Boxes className="h-5 w-5" />
            Lista de Moldes ({molds.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {molds.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Peça</TableHead>
                    <TableHead>Cavidades</TableHead>
                    <TableHead>Ciclo (s)</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead>Ciclos Atuais</TableHead>
                    <TableHead>Vida Útil</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {molds.map((mold) => {
                    const lifePercentage = (mold.current_cycles / mold.mold_life) * 100;
                    return (
                      <TableRow key={mold.id} data-testid={`mold-row-${mold.code}`}>
                        <TableCell className="font-mono font-bold">{mold.code}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {getPartName(mold.part_id)}
                        </TableCell>
                        <TableCell className="font-mono">{mold.cavities}</TableCell>
                        <TableCell className="font-mono">{mold.cycle_time}</TableCell>
                        <TableCell>
                          {mold.runner_type === "hot" ? "Quente" : "Frio"}
                        </TableCell>
                        <TableCell className="font-mono">
                          {mold.current_cycles.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                              <div
                                className={`h-full ${
                                  lifePercentage > 80
                                    ? "bg-red-500"
                                    : lifePercentage > 60
                                    ? "bg-amber-500"
                                    : "bg-emerald-500"
                                }`}
                                style={{ width: `${Math.min(lifePercentage, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {lifePercentage.toFixed(0)}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.location.href = `/molds/${mold.id}/full`}
                              data-testid={`fiche-mold-${mold.code}`}
                              className="text-blue-700 border-blue-300"
                            >
                              Ficha completa
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(mold)}
                              data-testid={`edit-mold-${mold.code}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(mold.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-mold-${mold.code}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <Boxes className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Nenhum molde registado</p>
              <p className="text-sm">Clique em "Novo Molde" para começar</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
